import { formatBookingAmount, formatBookingItems, getDirectBookingEngine } from './booking-data-tokens'
import { Injectable } from '@angular/core'
import * as lodash from 'lodash'
import { isFunction } from 'lodash'
import * as R from 'ramda'
import { Guest } from '@tv3/store/guest/guest.model'
import { DateDiffPipe } from '@tv3/pipes/utility/date-diff.pipe'
import { Inquiry } from '@tv3/store/inquiry/inquiry.model'
import {
  Account,
  DataCheckerService,
  DataEntityType,
  formatDate,
  isSomething,
  Rental,
  RentalService,
  selectRentalById,
  selectRentalSubUnits,
  toMoment,
} from '@tokeet-frontend/tv3-platform'
import { Website } from '@tv3/store/website/website.model'
import { CurrencyPipe } from '@angular/common'
import { combineLatest, isObservable, Observable, of } from 'rxjs'
import { catchError, concatMap, map, mapTo, switchMap, take, tap, toArray } from 'rxjs/operators'
import { CustomCodeView } from '@tv3/store/custom-codes/custom-code.model'
import { DiscountCode, DiscountCodeService, DiscountCodeType } from '@tokeet-frontend/discount-codes'
import { select, Store } from '@ngrx/store'
import { selectAllCustomCodeViews } from '@tv3/store/custom-codes/custom-code.selectors'
import { ActionFailed } from '@tokeet-frontend/tv3-platform'
import { BookingCostResolver } from '@tokeet/cost-resolver'
import { CustomCodeGuard } from '@tv3/guards/custom-code.guard'
import { InquiryService } from '@tv3/store/inquiry/inquiry.service'
import { SmartDeviceAccess } from '@tokeet-frontend/smart-devices'
import {
  EntityAttributeGuard,
  EntityAttributeResponse,
  getAttributeInfoFromToken,
  selectAllEntityAttributes,
} from '@tokeet-frontend/custom-attrs'
import { AuthService } from '@tv3/services/auth.service'

export enum DataDictTokenType {
  Unknown = 'unknown',
  Guest = 'guest',
  Inquiry = 'inquiry',
  Rental = 'rental',
  Device = 'device',
  CustomCode = 'code',
  Discount = 'discount',
  SecurityDeposit = 'securityDeposit',
  Attribute = 'attribute',
  Hold = 'hold',
}

export interface DataDictItemDef {
  type: DataDictTokenType
  value: string | Function
  tooltip?: string
}

interface DataDictResolveParams {
  account?: Account
  rental?: Rental
  inquiry?: Inquiry
  websites?: Website[]
  guest?: Guest
  accesses?: SmartDeviceAccess[]
  plainText?: boolean
}

interface DataDictResolveOptions {
  plainText?: boolean
}

const handleLineBreaks = (text: string, plainText = false) =>
  (text || '').replace(/\r?\n/g, plainText ? '\n' : '<br />')

const formatRentalCurrency = (rental?: Rental) => (value: number) =>
  new CurrencyPipe('en').transform(value, rental?.currency?.code || 'USD', 'symbol')

@Injectable({
  providedIn: 'root',
})
export class DataDictService {
  interpolate = /(\*\|[\s\S]+?\|\*)/gi

  dictDefs: { [key: string]: DataDictItemDef } = {
    '*|GUEST:NAME|*': {
      type: DataDictTokenType.Guest,
      tooltip: 'Will be pulled from the associated inquiry.',
      value: ({ guest }: DataDictResolveParams) => guest?.name || '',
    },
    '*|GUEST:FNAME|*': {
      type: DataDictTokenType.Guest,
      tooltip: `Will show only guest's first name in the message.`,
      value: ({ guest }: DataDictResolveParams) => R.head((guest?.name || '').split(' ')),
    },
    '*|GUEST:EMAIL|*': {
      type: DataDictTokenType.Guest,
      tooltip: 'Will be pulled from the associated inquiry.',
      value: ({ guest }: DataDictResolveParams) => guest?.primaryEmail || '',
    },
    '*|GUEST:PHONE|*': {
      type: DataDictTokenType.Guest,
      tooltip: 'Will be pulled from the associated inquiry (if provided) and is stored in the guest record.',
      value: ({ guest }: DataDictResolveParams) => guest?.phone || '',
    },
    '*|GUEST:guest_id|*': {
      type: DataDictTokenType.Guest,
      tooltip: `The AdvanceCM internal id of the guest`,
      value: ({ guest }: DataDictResolveParams) => guest?.id,
    },
    '*|GUEST:ADDRESS|*': {
      type: DataDictTokenType.Guest,
      tooltip: 'This is stored in the guest record (if provided).',
      value: ({ guest }: DataDictResolveParams) => (guest?.address && guest?.address?.address) || '',
    },
    '*|GUEST:CITY|*': {
      type: DataDictTokenType.Guest,
      tooltip: 'This is stored in the guest record (if provided).',
      value: ({ guest }: DataDictResolveParams) => (guest?.address && guest?.address?.city) || '',
    },
    '*|GUEST:COUNTRY|*': {
      type: DataDictTokenType.Guest,
      tooltip: 'This is stored in the guest record (if provided).',
      value: ({ guest }: DataDictResolveParams) => (guest?.address && guest?.address?.country) || '',
    },
    '*|GUEST:SOURCE|*': {
      type: DataDictTokenType.Guest,
      tooltip: 'Will be pulled from the associated inquiry.',
      value: ({ inquiry }: DataDictResolveParams) => inquiry?.inquirySource || '',
    },
    '*|GUEST:BIRTHDAY|*': {
      type: DataDictTokenType.Guest,
      tooltip: 'Will be pulled from the guest record which you can edit to include these details.',
      value: ({ guest }: DataDictResolveParams) => guest?.birthday || '',
    },
    '*|GUEST:SPOUSE|*': {
      type: DataDictTokenType.Guest,
      tooltip: 'Will be pulled from the guest record which you can edit to include these details.',
      value: ({ guest }: DataDictResolveParams) => guest?.wife || '',
    },
    '*|GUEST:HOBBY|*': {
      type: DataDictTokenType.Guest,
      tooltip: 'Will be pulled from the guest record which you can edit to include these details.',
      value: ({ guest }: DataDictResolveParams) => guest?.hobby || '',
    },
    '*|GUEST:RAND4|*': {
      type: DataDictTokenType.Guest,
      tooltip: 'Include a random 4 digit code which can be used to program smart locks.',
      value: () => lodash.random(1000, 9999),
    },
    '*|GUEST:RAND6|*': {
      type: DataDictTokenType.Guest,
      tooltip: 'Include a random 6 digit code which can be used to program smart locks.',
      value: () => lodash.random(100000, 999999),
    },

    // Inquiry data

    '*|INQUIRY:INQUIRY_ID|*': {
      type: DataDictTokenType.Inquiry,
      tooltip: 'This is imported from the listing channel and can be found in the notes section of the inquiry.',
      value: ({ inquiry }: DataDictResolveParams) => inquiry?.inquiryId || inquiry?.bookingId,
    },
    '*|INQUIRY:PKEY|*': {
      type: DataDictTokenType.Inquiry,
      tooltip: 'This is internal ID of the inquiry in AdvanceCM.',
      value: ({ inquiry }: DataDictResolveParams) => inquiry?.id,
    },
    '*|INQUIRY:CHANNEL|*': {
      type: DataDictTokenType.Inquiry,
      tooltip: 'The channel where we imported the associated inquiry from.',
      value: ({ inquiry }: DataDictResolveParams) => inquiry?.inquirySource || null,
    },
    '*|INQUIRY:ARRIVE|*': {
      type: DataDictTokenType.Inquiry,
      tooltip: 'The date that the guest is checking in.',
      value: ({ inquiry }: DataDictResolveParams) => formatDate(inquiry?.guestArrive, 'date', true),
    },
    '*|INQUIRY:DEPART|*': {
      type: DataDictTokenType.Inquiry,
      tooltip: 'The date the guest is due to check-out.',
      value: ({ inquiry }: DataDictResolveParams) => formatDate(inquiry?.guestDepart, 'date', true),
    },
    '*|INQUIRY:CHECK_IN|*': {
      type: DataDictTokenType.Inquiry,
      tooltip: 'The check-in time set for this rental under your rental Instructions tab in the rental details.',
      value: ({ inquiry, rental, account }: DataDictResolveParams) => {
        const checkInTime = RentalService.getCheckInTime(rental, inquiry?.checkIn, account)
        return checkInTime.format('H:mm')
      },
    },
    '*|INQUIRY:CHECK_OUT|*': {
      type: DataDictTokenType.Inquiry,
      tooltip: 'The check-out time set for this rental under your rental instructions tab in the rental details.',
      value: ({ inquiry, rental, account }: DataDictResolveParams) => {
        const checkOutTime = RentalService.getCheckOutTime(rental, inquiry?.checkOut, account)
        return checkOutTime.format('H:mm')
      },
    },
    '*|INQUIRY:COST|*': {
      type: DataDictTokenType.Inquiry,
      tooltip: 'The booking total which is shown on the associated inquiry.',
      value: ({ inquiry, rental }: DataDictResolveParams) => {
        const code = R.pathOr('USD', ['currency', 'code'], rental)
        let total
        if (inquiry?.bookingEngine) {
          total = inquiry?.bookingEngine?.total
        } else {
          const cost = BookingCostResolver.parseBookingCharges(inquiry as any)
          total = cost?.sum
        }
        return new CurrencyPipe('en').transform(total, code, 'symbol')
      },
    },
    '*|INQUIRY:RENTAL_PRICE|*': {
      type: DataDictTokenType.Inquiry,
      tooltip: $localize`Direct bookings only: the base rental charge for the entire stay, excluding fees and taxes.`,
      value: ({ inquiry, rental }: DataDictResolveParams) =>
        formatBookingAmount(getDirectBookingEngine(inquiry)?.base, formatRentalCurrency(rental)),
    },
    '*|INQUIRY:BOOKING_FEES|*': {
      type: DataDictTokenType.Inquiry,
      tooltip: $localize`Direct bookings only: each booking fee with its calculated amount.`,
      value: ({ inquiry, rental, plainText }: DataDictResolveParams) =>
        formatBookingItems(getDirectBookingEngine(inquiry)?.bookingFees, formatRentalCurrency(rental), plainText),
    },
    '*|INQUIRY:TAXES|*': {
      type: DataDictTokenType.Inquiry,
      tooltip: $localize`Direct bookings only: each tax, such as tourist tax, with its calculated amount.`,
      value: ({ inquiry, rental, plainText }: DataDictResolveParams) =>
        formatBookingItems(getDirectBookingEngine(inquiry)?.taxes, formatRentalCurrency(rental), plainText),
    },
    '*|INQUIRY:BOOKING_FORMULA|*': {
      type: DataDictTokenType.Inquiry,
      tooltip: 'Booking costs were adjusted via the booking formula set on the channel.',
      value: ({ inquiry, rental }: DataDictResolveParams) => {
        const code = R.pathOr('USD', ['currency', 'code'], rental)
        return this.inquiryService
          .getCostAfterFormula(inquiry?.id)
          .pipe(map((total) => new CurrencyPipe('en').transform(total, code, 'symbol')))
      },
    },
    '*|INQUIRY:NIGHTS|*': {
      type: DataDictTokenType.Inquiry,
      tooltip: 'The number of nights the guest is staying.',
      value: ({ inquiry }: DataDictResolveParams) =>
        this.dateDiffPipe.transform(inquiry?.guestDepart, inquiry?.guestArrive, true),
    },
    '*|INQUIRY:ADULTS|*': {
      type: DataDictTokenType.Inquiry,
      tooltip: 'The number of adults which is stored in the inquiry.',
      value: ({ inquiry }: DataDictResolveParams) => inquiry?.numAdults || 0,
    },
    '*|INQUIRY:CHILDREN|*': {
      type: DataDictTokenType.Inquiry,
      tooltip: 'The number of Children set in the inquiry details.',
      value: ({ inquiry }: DataDictResolveParams) => inquiry?.numChild || 0,
    },
    '*|INQUIRY:GUESTS|*': {
      type: DataDictTokenType.Inquiry,
      tooltip: 'The total guests (adults + children) stored in the inquiry.',
      value: ({ inquiry }: DataDictResolveParams) => (inquiry?.numAdults || 0) + (inquiry?.numChild || 0),
    },
    '*|INQUIRY:BOOK_DATE|*': {
      type: DataDictTokenType.Inquiry,
      tooltip: 'This is the date the booking was imported by AdvanceCM.',
      value: ({ inquiry }: DataDictResolveParams) => (inquiry?.booked ? formatDate(inquiry?.booked) : ''),
    },
    '*|INQUIRY:CHECKIN_ONLINE_URL|*': {
      type: DataDictTokenType.Inquiry,
      tooltip: 'Online Checkin URL',
      value: ({ inquiry }: DataDictResolveParams) => {
        return this.inquiryService.getOnlineCheckinUrl(inquiry.id)
      },
    },
    '*|INQUIRY:GUEST_PORTAL|*': {
      type: DataDictTokenType.Inquiry,
      tooltip: 'Include a URL directing the guest to your guest portal.',
      value: ({ inquiry, websites, plainText }: DataDictResolveParams) => {
        if (!isSomething(websites)) {
          return ''
        }
        const portal = R.last(R.filter((w) => !!w.isPortal, websites))
        if (isSomething(portal)) {
          const url = `https://${portal.domain}/authorize/${inquiry.account}/${inquiry.id}/${inquiry.guestId}/${inquiry.created}`
          return plainText ? url : `<a href="${url}">Click here</a>`
        } else {
          return ''
        }
      },
    },
    '*|INQUIRY:SECURITY_DOOR_CODE|*': {
      type: DataDictTokenType.Inquiry,
      tooltip: 'Door code for smart lock.',
      value: ({ inquiry }: DataDictResolveParams) =>
        this.inquiryService.getSecurityDoorCode(inquiry.id).pipe(map((t) => t?.door_code || '')),
    },
    '*|INQUIRY:SECURITY_SMART_DEVICES|*': {
      type: DataDictTokenType.Inquiry,
      tooltip: 'Codes for linked smart devices.',
      value: ({ accesses }: DataDictResolveParams) => {
        return lodash
          .map(accesses, (t) => {
            const start = formatDate(t.access.start, 'MMM DD • hh:mm A')
            const end = formatDate(t.access.end, 'MMM DD • hh:mm A')
            return `${t.device_name}: ${t.access.code} (${start} ~ ${end})`
          })
          .join(', ')
      },
    },

    // Rental data

    '*|RENTAL:NAME|*': {
      type: DataDictTokenType.Rental,
      tooltip: 'The AdvanceCM rental name.',
      value: ({ rental }: DataDictResolveParams) => rental?.name || '',
    },
    '*|RENTAL:ID|*': {
      type: DataDictTokenType.Rental,
      tooltip: 'The AdvanceCM internal id of the rental.',
      value: ({ rental }: DataDictResolveParams) => rental?.id || '',
    },
    '*|RENTAL:DISPLAY_NAME|*': {
      type: DataDictTokenType.Rental,
      tooltip: 'The AdvanceCM rental display name',
      value: ({ rental }: DataDictResolveParams) => rental?.displayName || '',
    },
    '*|RENTAL:PHONE|*': {
      type: DataDictTokenType.Rental,
      tooltip: 'The AdvanceCM rental phone number. Pulled from the Basic Info section of Rental Details.',
      value: ({ rental }: DataDictResolveParams) => rental?.phone || '',
    },
    '*|RENTAL:EMAIL|*': {
      type: DataDictTokenType.Rental,
      tooltip: 'The AdvanceCM rental email. Pulled from the Basic Info section of Rental Details.',
      value: ({ rental }: DataDictResolveParams) => rental?.email || '',
    },
    '*|RENTAL:MULTI_UNIT_NUMBER|*': {
      type: DataDictTokenType.Rental,
      tooltip: 'The unit number for a subunit. Pulled from the Basic Info section of Rental Details.',
      value: ({ rental }: DataDictResolveParams) => rental?.multi_unit_number || '',
    },
    '*|RENTAL:ZIP|*': {
      type: DataDictTokenType.Rental,
      tooltip: 'The AdvanceCM rental zip code. Pulled from the Basic Info section of Rental Details.',
      value: ({ rental }: DataDictResolveParams) =>
        (rental?.address && (rental?.address?.postalCode || rental?.address?.zip)) || '',
    },
    '*|RENTAL:ADDRESS|*': {
      type: DataDictTokenType.Rental,
      tooltip: 'The AdvanceCM rental address. Pulled from the Basic Info section of Rental Details.',
      value: ({ rental }: DataDictResolveParams) => (rental?.address && rental?.address?.address) || '',
    },
    '*|RENTAL:CITY|*': {
      type: DataDictTokenType.Rental,
      tooltip: 'The AdvanceCM rental city. Pulled from the Basic Info section of Rental Details.',
      value: ({ rental }: DataDictResolveParams) => (rental?.address && rental?.address?.city) || '',
    },
    '*|RENTAL:STATE|*': {
      type: DataDictTokenType.Rental,
      tooltip: 'The AdvanceCM rental state. Pulled from the Basic Info section of Rental Details.',
      value: ({ rental }: DataDictResolveParams) => (rental?.address && rental?.address?.state) || '',
    },
    '*|RENTAL:COUNTRY|*': {
      type: DataDictTokenType.Rental,
      tooltip: 'The AdvanceCM rental country. Pulled from the Basic Info section of Rental Details.',
      value: ({ rental }: DataDictResolveParams) => (rental?.address && rental?.address?.country) || '',
    },
    '*|RENTAL:DESCRIPTION|*': {
      type: DataDictTokenType.Rental,
      tooltip: 'Pulled from the description field on the Detail Information tab of Rental Details.',
      value: ({ rental }: DataDictResolveParams) => rental?.description || '',
    },
    '*|RENTAL:TERMS|*': {
      type: DataDictTokenType.Rental,
      tooltip: '',
      value: ({ rental }: DataDictResolveParams) => rental?.payment_terms || '',
    },
    '*|RENTAL:CUSTOM1|*': {
      type: DataDictTokenType.Rental,
      tooltip: 'Pulled from the Custom Information 1 field on the Custom Information tab of Rental Details.',
      value: ({ rental, plainText }: DataDictResolveParams) => handleLineBreaks(rental?.custom1, plainText),
    },
    '*|RENTAL:CUSTOM2|*': {
      type: DataDictTokenType.Rental,
      tooltip: 'Pulled from the Custom Information 2 field on the Custom Information tab of Rental Details.',
      value: ({ rental, plainText }: DataDictResolveParams) => handleLineBreaks(rental?.custom2, plainText),
    },
    '*|RENTAL:CUSTOM3|*': {
      type: DataDictTokenType.Rental,
      tooltip: 'Pulled from the Custom Information 3 field on the Custom Information tab of Rental Details.',
      value: ({ rental, plainText }: DataDictResolveParams) => handleLineBreaks(rental?.custom3, plainText),
    },
    '*|RENTAL:CUSTOM4|*': {
      type: DataDictTokenType.Rental,
      tooltip: 'Pulled from the Custom Information 4 field on the Custom Information tab of Rental Details.',
      value: ({ rental, plainText }: DataDictResolveParams) => handleLineBreaks(rental?.custom4, plainText),
    },
    '*|RENTAL:CUSTOM5|*': {
      type: DataDictTokenType.Rental,
      tooltip: 'Pulled from the Custom Information 5 field on the Custom Information tab of Rental Details.',
      value: ({ rental, plainText }: DataDictResolveParams) => handleLineBreaks(rental?.custom5, plainText),
    },
    '*|RENTAL:CHECKIN_INSTRUCTIONS|*': {
      type: DataDictTokenType.Rental,
      tooltip: 'Pulled from the Check-in Instructions field on the Rental Instructions tab of Rental Details.',
      value: ({ rental }: DataDictResolveParams) => (rental?.instructions && rental?.instructions?.checkin) || '',
    },
    '*|RENTAL:CHECKOUT_INSTRUCTIONS|*': {
      type: DataDictTokenType.Rental,
      tooltip: 'Pulled from the Checkout Instructions field on the Rental Instructions tab of Rental Details.',
      value: ({ rental }: DataDictResolveParams) => (rental?.instructions && rental?.instructions?.checkout) || '',
    },
    '*|RENTAL:HOUSE_RULES|*': {
      type: DataDictTokenType.Rental,
      tooltip: 'Pulled from the House Rules field on the Rental Instructions tab of Rental Details.',
      value: ({ rental }: DataDictResolveParams) => (rental?.instructions && rental?.instructions?.rules) || '',
    },
    '*|RENTAL:DIRECTIONS|*': {
      type: DataDictTokenType.Rental,
      tooltip: 'Pulled from the Directions field on the Rental Instructions tab of Rental Details.',
      value: ({ rental }: DataDictResolveParams) => (rental?.instructions && rental?.instructions?.directions) || '',
    },
    '*|RENTAL:PAYMENT_INSTRUCTIONS|*': {
      type: DataDictTokenType.Rental,
      value: ({ rental, plainText }: DataDictResolveParams) =>
        handleLineBreaks(rental?.payment_instructions, plainText),
    },
    '*|RENTAL:WIFI_NAME|*': {
      type: DataDictTokenType.Rental,
      tooltip: 'Pulled from the WiFi name field on the Rental Instructions tab of Rental Details.',
      value: ({ rental }: DataDictResolveParams) => (rental?.specifics && rental?.specifics?.wifi_name) || '',
    },
    '*|RENTAL:WIFI|*': {
      type: DataDictTokenType.Rental,
      tooltip: 'Pulled from the WiFi password field on the Rental Instructions tab of Rental Details.',
      value: ({ rental }: DataDictResolveParams) => (rental?.specifics && rental?.specifics?.wifi_pass) || '',
    },
    '*|RENTAL:KEYPICKUP|*': {
      type: DataDictTokenType.Rental,
      tooltip: 'Pulled from the Key Pickup field on the Rental Instructions tab of Rental Details.',
      value: ({ rental }: DataDictResolveParams) => (rental?.specifics && rental?.specifics?.key_pickup) || '',
    },
    '*|RENTAL:SECURITY|*': {
      type: DataDictTokenType.Rental,
      tooltip: 'Pulled from the Codes and Passwords section on your Rental Instructions tab of Rental Details.',
      value: ({ rental }: DataDictResolveParams) => (rental?.specifics && rental?.specifics?.sec_code) || '',
    },
    '*|RENTAL:SPECIAL|*': {
      type: DataDictTokenType.Rental,
      tooltip: 'Pulled from the Special Instructions field on Rental Instructions tab of Rental Details.',
      value: ({ rental }: DataDictResolveParams) => (rental?.specifics && rental?.specifics?.special_inst) || '',
    },
    '*|RENTAL:WEBSITES|*': {
      type: DataDictTokenType.Rental,
      tooltip: 'Will include the URL for the tokeet website this rental is associated with.',
      value: ({ websites, plainText }: DataDictResolveParams) => {
        return R.reduce(
          (acc, web) => {
            const url = 'https://' + web.domain
            acc += plainText ? `${web.title}: ${url}\n` : `<a href="${url}" target="_blank">${web.title}</a>&nbsp;`
            return acc
          },
          '',
          websites
        ).trim()
      },
    },
    // Device
    '*|DEVICE:NAME|*': {
      type: DataDictTokenType.Device,
      value: ({ accesses }: DataDictResolveParams) => this.getPrimaryDeviceAccess(accesses)?.device_name || '',
    },
    '*|DEVICE:RENTAL|*': {
      type: DataDictTokenType.Device,
      value: ({ rental }: DataDictResolveParams) => rental?.name || '',
    },
    '*|DEVICE:CODE|*': {
      type: DataDictTokenType.Device,
      value: ({ accesses }: DataDictResolveParams) => this.getPrimaryDeviceAccess(accesses)?.access?.code || '',
    },
    '*|DEVICE:CODE_STARTDATE|*': {
      type: DataDictTokenType.Device,
      value: ({ accesses }: DataDictResolveParams) =>
        formatDate(this.getPrimaryDeviceAccess(accesses)?.access?.start, 'datetime', true, ''),
    },
    '*|DEVICE:CODE_ENDDATE|*': {
      type: DataDictTokenType.Device,
      value: ({ accesses }: DataDictResolveParams) =>
        formatDate(this.getPrimaryDeviceAccess(accesses)?.access?.end, 'datetime', true, ''),
    },

    // Security Deposit
    '*|SECURITY_DEPOSIT:LINK|*': {
      type: DataDictTokenType.SecurityDeposit,
      tooltip: $localize`Will show a link to the guest-facing security deposit page.`,
      value: '',
    },

    // Discount
    '*|DISCOUNT:5|*': {
      type: DataDictTokenType.Discount,
      value: () => '',
    },
    '*|DISCOUNT:10|*': {
      type: DataDictTokenType.Discount,
      value: () => '',
    },
    '*|DISCOUNT:15|*': {
      type: DataDictTokenType.Discount,
      value: () => '',
    },
    '*|DISCOUNT:20|*': {
      type: DataDictTokenType.Discount,
      value: () => '',
    },

    // Hold Event
    '*|HOLD:TITLE|*': {
      type: DataDictTokenType.Hold,
      value: () => '',
    },
    '*|HOLD:DESCRIPTION|*': {
      type: DataDictTokenType.Hold,
      value: () => '',
    },
    '*|HOLD:START|*': {
      type: DataDictTokenType.Hold,
      value: () => '',
    },
    '*|HOLD:END|*': {
      type: DataDictTokenType.Hold,
      value: () => '',
    },
    '*|HOLD:EXPIRES|*': {
      type: DataDictTokenType.Hold,
      value: () => '',
    },
  }

  constructor(
    private store: Store<any>,
    private auth: AuthService,
    private dateDiffPipe: DateDiffPipe,
    private dataCheckerService: DataCheckerService,
    private discountCodeService: DiscountCodeService,
    private inquiryService: InquiryService
  ) {
    this.dataCheckerService.check([CustomCodeGuard, EntityAttributeGuard])
  }

  private getPrimaryDeviceAccess(items: SmartDeviceAccess[]) {
    const device = lodash.find(items, (t) => !!t.is_primary)
    return device ? device : lodash.head(items)
  }

  getAvailableKeys(): { [type: string]: { value: string; tooltip?: string }[] } {
    return lodash.groupBy(
      lodash.map(this.dictDefs, (def: DataDictItemDef, key: string) => {
        return {
          type: def.type,
          value: key,
          tooltip: def.tooltip,
        }
      }),
      'type'
    )
  }

  getAllDataTokensInText(text): string[] {
    let m
    const tokens = []
    do {
      m = this.interpolate.exec(text)
      if (m) {
        tokens.push(m[1])
      }
    } while (m)

    return tokens
  }

  resolveTokens(tokens: string[], params: DataDictResolveParams): Observable<{ [key: string]: any }> {
    const rental = params.rental
    return combineLatest([
      this.store.pipe(select(selectAllCustomCodeViews)),
      this.store.pipe(select(selectAllEntityAttributes)),
      rental?.multi_unit_id ? this.store.pipe(select(selectRentalById(rental.multi_unit_id))) : of(null),
    ]).pipe(
      take(1),
      switchMap(([codes, attrs, multiUnit]) => {
        const tokenValuePairs = {}
        return of(...tokens).pipe(
          map((t) => lodash.trim(t)),
          concatMap((token) => {
            const tokenType = this.getTokenType(token)
            let ob: Observable<any> | string
            switch (tokenType) {
              case DataDictTokenType.Attribute:
                ob = this.resolveEntityAttr(token, attrs, { ...params, rental: multiUnit || rental })
                break
              case DataDictTokenType.CustomCode:
                ob = this.resolveCustomCode(token, codes, rental?.id)
                break
              case DataDictTokenType.Discount:
                ob = this.resolveDiscount(token, params)
                break
              default:
                ob = this.getResolver(params)(token)
            }

            ob = isObservable(ob) ? ob : of(ob)
            return ob.pipe(
              tap((v) => (tokenValuePairs[token] = v)),
              catchError(() => of(''))
            )
          }),
          toArray(),
          mapTo(tokenValuePairs)
        )
      })
    )
  }

  resolve(
    text: string,
    params: DataDictResolveParams,
    additionalDataTokenPairs: { [key: string]: string } = {},
    options: DataDictResolveOptions = {}
  ): Observable<string> {
    text = text || ''
    if (!text) return of('')

    const escapeRegExp = (str: string) => {
      return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') // $& means the whole matched string
    }

    let tokens = this.getAllDataTokensInText(text)
    tokens = [...tokens, ...Object.keys(additionalDataTokenPairs)]

    const paramsWithOptions: DataDictResolveParams = { ...(params || {}), plainText: options.plainText }

    return this.resolveTokens(tokens, paramsWithOptions).pipe(
      map((pairs) => {
        pairs = { ...pairs, ...additionalDataTokenPairs }
        return lodash.reduce(
          tokens,
          (t, token) => {
            return t.replace(new RegExp(escapeRegExp(token), 'g'), () =>
              lodash.isNil(pairs[token]) ? '' : pairs[token]
            )
          },
          text
        )
      })
    )
  }

  private getResolver(params: DataDictResolveParams) {
    return (key: string) => {
      const item = this.dictDefs[key]
      if (isFunction(item?.value)) {
        return item.value(params)
      }
      return item?.value || ''
    }
  }

  private resolveCustomCode(token: string, items: CustomCodeView[], rentalId?: string) {
    const customCode = R.find(
      (c) => c.nameFormatted === token && (R.isEmpty(c.rentals) || R.contains(rentalId, c.rentals)),
      items
    )
    if (R.isNil(customCode)) {
      return
    }
    return customCode.body
  }

  getDiscountToken(token: string): { amount: number; type: DiscountCodeType } {
    const match = /^\*\|DISCOUNT:(-?[0-9]+)(%?)\|\*$/.exec(token.trim())
    if (!match) {
      return null
    }
    const [t, amount, type] = match
    return { amount: parseFloat(amount), type: type === '%' ? DiscountCodeType.Percent : DiscountCodeType.Flat }
  }

  resolveDiscount(token: string, { guest, rental }: DataDictResolveParams): Observable<string> {
    const data = this.getDiscountToken(token)
    if (!data) return of('')

    return (
      this.discountCodeService
        // @ts-ignore
        .create({
          amount: data.amount,
          type: data.type,
          max_uses: 1,
          email: guest.primaryEmail,
          name: `Discount for ${guest.name}`,
          description: `Created automatically in message.`,
          start: toMoment().startOf('day').unix(),
          expires: toMoment().add(1, 'year').unix(),
          rental_id: rental.id,
        })
        .pipe(
          map((discount: DiscountCode) => {
            return `${discount.code}`
          }),
          catchError((error) => {
            this.store.dispatch(ActionFailed({ error }))
            return of('')
          })
        )
    )
  }

  private resolveEntityAttr(token: string, items: EntityAttributeResponse[], params: DataDictResolveParams) {
    const [type, key] = getAttributeInfoFromToken(token)
    if (!type) return ''
    let entityId: string
    switch (type) {
      case DataEntityType.Rental:
        entityId = params.rental?.id
        break
      case DataEntityType.Guest:
        entityId = params.guest?.id
        break
      case DataEntityType.Inquiry:
        entityId = params.inquiry?.id
        break
      case DataEntityType.User:
        entityId = this.auth.user.id
        break
      default:
        return ''
    }

    const item = lodash.find(items, (t) => t.entity_type === type && t.entity_pkey === entityId)
    return item?.attributes?.[key]
  }

  getTokenType(token: string) {
    if (/^\*\|GUEST:/.test(token)) {
      return DataDictTokenType.Guest
    } else if (/^\*\|INQUIRY:/.test(token)) {
      return DataDictTokenType.Inquiry
    } else if (/^\*\|RENTAL:/.test(token)) {
      return DataDictTokenType.Rental
    } else if (/^\*\|DEVICE:/.test(token)) {
      return DataDictTokenType.Device
    } else if (/^\*\|DISCOUNT:/.test(token)) {
      return DataDictTokenType.Discount
    } else if (/^\*\|SECURITY_DEPOSIT:/.test(token)) {
      return DataDictTokenType.SecurityDeposit
    } else if (/^\*\|CUSTOM:/.test(token)) {
      return DataDictTokenType.CustomCode
    } else if (/^\*\|[A-Z]+_ATTRS:/.test(token)) {
      return DataDictTokenType.Attribute
    } else {
      return DataDictTokenType.Unknown
    }
  }
}
